/**
 * Serialized, coalescing writes of live service state (active rooms and battle sessions, design D5): one write chain
 * per document key, so the writes of a document reach the storage in the order they were scheduled, and a write
 * scheduled while an earlier one of the same document is still waiting to start replaces it (the snapshot is built
 * when the write starts, so it is always the newest state). A failed write is logged; the next change writes again.
 */
import type { Logger } from './logger';
import type { StorageAdapter, StorageBatchOp } from './storage';

export class LiveDocWriter {
  private readonly chains = new Map<string, Promise<void>>();
  private readonly queued = new Map<string, () => StorageBatchOp[]>();

  constructor(
    private readonly storage: StorageAdapter,
    private readonly logger: Logger,
  ) {}

  /** Writes the ops `build` returns when the write starts (after the key's earlier writes); resolves when written. */
  schedule(key: string, build: () => StorageBatchOp[]): Promise<void> {
    const waiting = this.queued.has(key);
    this.queued.set(key, build);
    const current = this.chains.get(key);
    if (waiting && current) return current;
    const next = (current ?? Promise.resolve())
      .then(async () => {
        const b = this.queued.get(key);
        this.queued.delete(key);
        if (!b) return;
        const ops = b();
        if (ops.length > 0) await this.storage.batch(ops);
      })
      .catch((err) => this.logger.error('live state write failed', { key, err }));
    this.chains.set(key, next);
    void next.then(() => {
      if (this.chains.get(key) === next) this.chains.delete(key);
    });
    return next;
  }

  /** Resolves when every scheduled write has been written (or failed). */
  async idle(): Promise<void> {
    while (this.chains.size > 0) await Promise.all([...this.chains.values()]);
  }
}
